<template>
  <div>
    <KTCard ref="preview">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-fuel-expenses' }">{{
            $t("MENU.ITEM.EXP.FUELS_EXPENSES")
          }}</router-link>
          <i class="mr-2"></i>
          <small class="">{{ $t("LABELS.EDIT") }}</small>
        </h3>
      </template>
      <template v-slot:toolbar>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <DeleteButton
            v-on:onDelete="onDelete()"
            v-permission="['tank-charges-delete']"
          ></DeleteButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingFuelExpense"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
      <template v-slot:body>
        <FuelExpensesForm
          ref="FuelExpensesForm"
          :form="form"
          @add-row="handleAddRow"
          @track-deleted-row="trackDeletedRow"
          @track-deleted-tank-charge="trackDeletedTankCharge"
        ></FuelExpensesForm>
      </template>
      <template v-slot:toolbarfoot>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <DeleteButton
            v-on:onDelete="onDelete()"
            v-permission="['tank-charges-delete']"
          ></DeleteButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingFuelExpense"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
    </KTCard>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  FETCH_FUEL_EXPENSE,
  UPDATE_FUEL_EXPENSE,
  DELETE_FUEL_EXPENSE
} from "@/modules/fuel-expenses/store/fuelexpenses.module";

import SaveButtonDropdown from "@/view/content/forms/components/SaveButtonDropdown.vue";
import KTCard from "@/view/content/Card.vue";
import FuelExpensesForm from "@/modules/fuel-expenses/components/FuelExpensesForm.vue";

export default {
  name: "FuelExpensesEdit",
  components: {
    KTCard,
    SaveButtonDropdown,
    FuelExpensesForm
  },
  data() {
    return {
      loading: true,
      deletedRowIds: [],
      deletedTankChargeIds: [],
      form: {
        gasTankStationId: null,
        invoiceNumber: null,
        invoiceDate: null,
        qty: 1,
        totalNoVat: "0.00",
        totalWithVat: "0.00",
        vat: "0.00",
        toAccounting: true,
        rows: []
      }
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.EXP.FUELS_EXPENSES"),
        route: { name: "list-fuel-expenses" }
      },
      { title: this.$i18n.t("LABELS.EDIT") }
    ]);

    this.$store
      .dispatch(FETCH_FUEL_EXPENSE, this.$route.params.id)
      .then(data => {
        this.form = data.data;
        this.loading = false;
      });
  },
  methods: {
    handleAddRow() {
      this.onSave("add-row");
    },
    trackDeletedRow(rowId) {
      if (rowId && !this.deletedRowIds.includes(rowId)) {
        this.deletedRowIds.push(rowId);
      }
    },
    trackDeletedTankCharge(chargeId) {
      if (chargeId && !this.deletedTankChargeIds.includes(chargeId)) {
        this.deletedTankChargeIds.push(chargeId);
      }
    },
    preparePayload(form) {
      const payload = {
        gasTankStationId: form.gasTankStationId,
        invoiceNumber: form.invoiceNumber,
        invoiceDate: form.invoiceDate,
        qty: form.qty,
        totalNoVat: form.totalNoVat,
        totalWithVat: form.totalWithVat,
        vat: form.vat,
        toAccounting: form.toAccounting,
        rows: form.rows.map(row => ({
          id: row.id,
          fuelTypeId: row.fuelTypeId,
          price: row.price,
          totalQty: row.totalQty,
          totalPrice: row.totalPrice,
          tankCharges: (row.tankCharges || []).map(charge => ({
            id: charge.id,
            gasTankStationId: charge.gasTankStationId,
            staffId: charge.staffId,
            fuelable: {
              id: charge.fuelableId,
              type: charge.fuelableType,
              name: charge.fuelableName
            },
            liters: charge.liters,
            mileage: charge.mileage,
            isMileage: charge.isMileage,
            dateCharged: charge.dateCharged,
            staffName: charge.staffName
          }))
        }))
      };

      if (this.deletedRowIds.length > 0) {
        payload.deletedRowIds = this.deletedRowIds;
      }
      if (this.deletedTankChargeIds.length > 0) {
        payload.deletedTankChargeIds = this.deletedTankChargeIds;
      }

      return payload;
    },
    onSave(next) {
      let vm = this;

      this.$refs.FuelExpensesForm.$v.form.$touch();
      if (this.$refs.FuelExpensesForm.$v.form.$anyError) {
        const errorMessages = [];

        if (this.$refs.FuelExpensesForm.$v.form.gasTankStationId.$error) {
          errorMessages.push(this.$t("MENU.ITEM.TANKS.GAS_STATION"));
        }
        if (this.$refs.FuelExpensesForm.$v.form.invoiceNumber.$error) {
          errorMessages.push(this.$t("FORM_LABELS.INVOICE_NUMBER"));
        }
        if (this.$refs.FuelExpensesForm.$v.form.invoiceDate.$error) {
          errorMessages.push(this.$t("FORM_LABELS.DATE"));
        }

        const errorText =
          errorMessages.length > 0
            ? `${this.$t(
                "ALERTS.MISSING_REQUIRED_FIELDS"
              )}: ${errorMessages.join(", ")}`
            : this.$t("ALERTS.FILL_REQUIRED_FIELDS");

        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> " + this.$t("ALERTS.WARNING"),
          text: errorText
        });
        return;
      }

      // Validate rows have tank charges
      if (!this.$refs.FuelExpensesForm.validateRows()) {
        const errorText =
          this.$t("ALERTS.TANK_CHARGE_REQUIRED") ||
          "Each fuel row must have at least one tank charge";
        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> " + this.$t("ALERTS.WARNING"),
          text: errorText
        });
        return;
      }

      const payload = this.preparePayload(this.form);

      this.$store
        .dispatch(UPDATE_FUEL_EXPENSE, {
          id: this.$route.params.id,
          payload: payload
        })
        .then(data => {
          let itemEdit = data.data;

          vm.deletedRowIds = [];
          vm.deletedTankChargeIds = [];

          vm.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });

          if (next == "continue") {
            this.$router.push({
              name: "edit-fuel-expenses",
              params: { id: itemEdit.id }
            });
          }
          if (next == "add-row") {
            this.$router.push({
              name: "add-fuel-expenses-row",
              params: { expenseId: itemEdit.id }
            });
          }
          if (next == "new") {
            this.$router.push({ name: "add-fuel-expenses" });
          }
          if (next == "exit") {
            this.$router.push({ name: "list-fuel-expenses" });
          }
        })
        .catch(response => {
          let errorText = response.data.message;
          if (response.data && response.data.errors) {
            const formattedErrors = this.$errors.format(response.data.errors);
            if (formattedErrors.length > 0) {
              errorText = formattedErrors.join(", ");
            }
          }

          this.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: errorText
          });
        });
    },
    onDelete() {
      this.$dialog.confirm(this.$t("BUTTON.PLEASE_CONFIRM")).then(() => {
        this.$store
          .dispatch(DELETE_FUEL_EXPENSE, this.$route.params.id)
          .then(data => {
            this.$notify({
              group: "notify",
              type: "success",
              title: "<i class='flaticon2-checkmark'></i> Success",
              text: data.message
            });
            this.$router.push({ name: "list-fuel-expenses" });
          })
          .catch(response => {
            this.$notify({
              group: "notify",
              type: "error",
              title: this.$t("ALERTS.ERROR"),
              text: response.data.message
            });
          });
      });
    }
  }
};
</script>
